import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, InputGroup, Tabs, Tab, Modal } from 'react-bootstrap';
import { Search, Filter, Download, RefreshCw, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import { getAuditLogs, getReactionLogs, getSystemEvents } from '../services/logs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Logs = () => {
  const [activeTab, setActiveTab] = useState('audit');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [reactionLogs, setReactionLogs] = useState([]);
  const [systemEvents, setSystemEvents] = useState([]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Filter states for audit logs
  const [auditFilters, setAuditFilters] = useState({
    table_name: '',
    operation: '',
    start_date: '',
    end_date: ''
  });
  
  // Filter states for reaction logs
  const [reactionFilters, setReactionFilters] = useState({
    success: '',
    token_id: '',
    proxy_id: '',
    queue_id: '',
    start_date: '',
    end_date: ''
  });

  // Filter states for system events
  const [systemFilters, setSystemFilters] = useState({
    event_type: '',
    severity: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchLogs();
  }, [activeTab]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      if (activeTab === 'audit') {
        const params = {
          skip: 0,
          limit: 100,
          ...auditFilters
        };
        
        // Clean empty string values
        Object.keys(params).forEach(key => {
          if (params[key] === '') {
            params[key] = null;
          }
        });
        
        const data = await getAuditLogs(params);
        setAuditLogs(data);
      } else if (activeTab === 'reactions') {
        const params = {
          skip: 0,
          limit: 100,
          ...reactionFilters
        };
        
        // Clean empty string values
        Object.keys(params).forEach(key => {
          if (params[key] === '') {
            params[key] = null;
          }
        });
        
        const data = await getReactionLogs(params);
        setReactionLogs(data);
      } else if (activeTab === 'system') {
        const params = {
          skip: 0,
          limit: 100,
          ...systemFilters
        };
        
        // Clean empty string values
        Object.keys(params).forEach(key => {
          if (params[key] === '') {
            params[key] = null;
          }
        });
        
        const data = await getSystemEvents(params);
        setSystemEvents(data);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchLogs();
  };

  const handleAuditFilterChange = (field, value) => {
    setAuditFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleReactionFilterChange = (field, value) => {
    // Convert empty strings to null for API compatibility
    const cleanedValue = value === '' ? null : value;
    setReactionFilters(prev => ({ ...prev, [field]: cleanedValue }));
  };

  const handleSystemFilterChange = (field, value) => {
    setSystemFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    fetchLogs();
  };

  const clearFilters = () => {
    if (activeTab === 'audit') {
      setAuditFilters({
        table_name: '',
        operation: '',
        start_date: '',
        end_date: ''
      });
    } else if (activeTab === 'reactions') {
      setReactionFilters({
        success: '',
        token_id: '',
        proxy_id: '',
        queue_id: '',
        start_date: '',
        end_date: ''
      });
    } else {
      setSystemFilters({
        event_type: '',
        severity: '',
        start_date: '',
        end_date: ''
      });
    }
  };

  const toggleRowExpansion = (logId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(logId)) {
      newExpandedRows.delete(logId);
    } else {
      newExpandedRows.add(logId);
    }
    setExpandedRows(newExpandedRows);
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text(`System ${activeTab === 'audit' ? 'Audit' : activeTab === 'reactions' ? 'Reaction' : 'System'} Logs`, 14, 15);
      doc.setFontSize(11);
      doc.setTextColor(100);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);

      let tableData = [];
      let tableColumns = [];

      if (activeTab === 'audit') {
        tableColumns = [
          { header: "Timestamp", dataKey: "timestamp" },
          { header: "Table", dataKey: "table" },
          { header: "Operation", dataKey: "operation" },
          { header: "Record ID", dataKey: "record_id" }
        ];

        tableData = auditLogs.map(log => ({
          timestamp: new Date(log.changed_at).toLocaleString(),
          table: log.table_name,
          operation: log.operation,
          record_id: log.record_id
        }));
      } else if (activeTab === 'reactions') {
        tableColumns = [
          { header: "Timestamp", dataKey: "timestamp" },
          { header: "Status", dataKey: "status" },
          { header: "Channel ID", dataKey: "channel_id" },
          { header: "Message ID", dataKey: "message_id" },
          { header: "Emoji", dataKey: "emoji" },
          { header: "Type", dataKey: "type" },
          { header: "Message", dataKey: "message" },
          { header: "Status Code", dataKey: "status_code" },
          { header: "Latency (ms)", dataKey: "latency" }
        ];

        tableData = reactionLogs.map(log => ({
          timestamp: new Date(log.created_at).toLocaleString(),
          status: log.success ? 'Success' : 'Failed',
          channel_id: log.channel_id,
          message_id: log.message_id,
          emoji: formatEmoji(log.emoji),
          type: log.reactor_type,
          message: log.error_message ? truncateText(log.error_message, 30) : 'N/A',
          status_code: log.status_code || 'N/A',
          latency: log.latency_ms || 'N/A'
        }));
      } else {
        tableColumns = [
          { header: "Timestamp", dataKey: "timestamp" },
          { header: "Event Type", dataKey: "event_type" },
          { header: "Severity", dataKey: "severity" },
          { header: "Message", dataKey: "message" }
        ];

        tableData = systemEvents.map(event => ({
          timestamp: new Date(event.created_at).toLocaleString(),
          event_type: event.event_type,
          severity: event.severity,
          message: event.message
        }));
      }

      autoTable(doc, {
        columns: tableColumns,
        body: tableData,
        startY: 30,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [66, 139, 202] },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        margin: { top: 30 }
      });

      doc.save(`${activeTab}-logs-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again or check the console for details.');
    }
  };

  const getOperationBadgeVariant = (operation) => {
    switch (operation?.toLowerCase()) {
      case 'insert': return 'success';
      case 'update': return 'warning';
      case 'delete': return 'danger';
      default: return 'secondary';
    }
  };

  const getSuccessBadgeVariant = (success) => {
    return success ? 'success' : 'danger';
  };

  const getSeverityBadgeVariant = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'info': return 'info';
      case 'warning': return 'warning';
      case 'error': return 'danger';
      case 'critical': return 'dark';
      default: return 'secondary';
    }
  };

  const formatEmoji = (emoji) => {
    if (!emoji || emoji.trim() === '') return 'N/A';
    try {
      const decoded = decodeURIComponent(emoji);
      return decoded;
    } catch (error) {
      return emoji;
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const viewMessage = (message) => {
    setCurrentMessage(message || 'No message content');
    setShowMessageModal(true);
  };

  const filteredAuditLogs = auditLogs.filter(log => 
    log.table_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.operation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.record_id?.toString().includes(searchTerm)
  );

  const filteredReactionLogs = reactionLogs.filter(log =>
    log.channel_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.message_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    formatEmoji(log.emoji)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.reactor_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (log.error_message && log.error_message.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredSystemEvents = systemEvents.filter(event =>
    event.event_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.severity?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentLogs = activeTab === 'audit' ? filteredAuditLogs : 
                     activeTab === 'reactions' ? filteredReactionLogs : 
                     filteredSystemEvents;

  return (
    <div>
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div className="mb-3 mb-md-0">
          <h1 className="h2 fw-bold mb-1">System Logs</h1>
          <p className="text-muted mb-0">
            Monitor system events, errors, and activity logs
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={handleRefresh} disabled={loading}>
            <RefreshCw size={16} className={`me-2 ${loading ? 'spin' : ''}`} />
            <span className="d-none d-md-inline">Refresh</span>
          </Button>
          <Button variant="primary" onClick={exportToPDF}>
            <Download size={16} className="me-2" />
            <span className="d-none d-md-inline">Export</span>
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-0">
          <Tabs
            activeKey={activeTab}
            onSelect={(tab) => setActiveTab(tab)}
            className="px-3 pt-3"
          >
            <Tab eventKey="audit" title="Audit Logs" />
            <Tab eventKey="reactions" title="Reaction Logs" />
            <Tab eventKey="system" title="System Events" />
          </Tabs>
        </Card.Body>
      </Card>

      {/* Filters */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <InputGroup size="sm">
                <InputGroup.Text>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search logs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            
            {activeTab === 'audit' ? (
              <>
                <Col xs={12} md={2}>
                  <Form.Control
                    type="text"
                    placeholder="Table name"
                    value={auditFilters.table_name}
                    onChange={(e) => handleAuditFilterChange('table_name', e.target.value)}
                    size="sm"
                  />
                </Col>
                <Col xs={12} md={2}>
                  <Form.Select
                    value={auditFilters.operation}
                    onChange={(e) => handleAuditFilterChange('operation', e.target.value)}
                    size="sm"
                  >
                    <option value="">All Operations</option>
                    <option value="INSERT">INSERT</option>
                    <option value="UPDATE">UPDATE</option>
                    <option value="DELETE">DELETE</option>
                  </Form.Select>
                </Col>
              </>
            ) : activeTab === 'reactions' ? (
              <>
                <Col xs={12} md={2}>
                  <Form.Select
                    value={reactionFilters.success}
                    onChange={(e) => handleReactionFilterChange('success', e.target.value)}
                    size="sm"
                  >
                    <option value="">All Status</option>
                    <option value="true">Success</option>
                    <option value="false">Failed</option>
                  </Form.Select>
                </Col>
                <Col xs={12} md={2}>
                  <Form.Control
                    type="number"
                    placeholder="Token ID"
                    value={reactionFilters.token_id}
                    onChange={(e) => handleReactionFilterChange('token_id', e.target.value)}
                    size="sm"
                  />
                </Col>
              </>
            ) : (
              <>
                <Col xs={12} md={2}>
                  <Form.Control
                    type="text"
                    placeholder="Event type"
                    value={systemFilters.event_type}
                    onChange={(e) => handleSystemFilterChange('event_type', e.target.value)}
                    size="sm"
                  />
                </Col>
                <Col xs={12} md={2}>
                  <Form.Select
                    value={systemFilters.severity}
                    onChange={(e) => handleSystemFilterChange('severity', e.target.value)}
                    size="sm"
                  >
                    <option value="">All Severity</option>
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="error">Error</option>
                    <option value="critical">Critical</option>
                  </Form.Select>
                </Col>
              </>
            )}
            
            <Col xs={12} md={2}>
              <div className="d-flex gap-2">
                <Button variant="primary" onClick={applyFilters} size="sm">
                  Apply
                </Button>
                <Button variant="outline-secondary" onClick={clearFilters} size="sm">
                  Clear
                </Button>
              </div>
            </Col>
          </Row>

          {/* Date Filters */}
          <Row className="g-3 mt-2">
            <Col xs={12} md={3}>
              <Form.Label className="small text-muted">Start Date</Form.Label>
              <Form.Control
                type="datetime-local"
                value={activeTab === 'audit' ? auditFilters.start_date : 
                      activeTab === 'reactions' ? reactionFilters.start_date : 
                      systemFilters.start_date}
                onChange={(e) => activeTab === 'audit' 
                  ? handleAuditFilterChange('start_date', e.target.value)
                  : activeTab === 'reactions'
                  ? handleReactionFilterChange('start_date', e.target.value)
                  : handleSystemFilterChange('start_date', e.target.value)
                }
                size="sm"
              />
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="small text-muted">End Date</Form.Label>
              <Form.Control
                type="datetime-local"
                value={activeTab === 'audit' ? auditFilters.end_date : 
                      activeTab === 'reactions' ? reactionFilters.end_date : 
                      systemFilters.end_date}
                onChange={(e) => activeTab === 'audit' 
                  ? handleAuditFilterChange('end_date', e.target.value)
                  : activeTab === 'reactions'
                  ? handleReactionFilterChange('end_date', e.target.value)
                  : handleSystemFilterChange('end_date', e.target.value)
                }
                size="sm"
              />
            </Col>
            <Col xs={12} md={6} className="d-flex align-items-end">
              <div className="text-muted small">
                Showing {currentLogs.length} logs
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Logs Table */}
      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          {/* Desktop Table */}
          <div className="d-none d-md-block table-responsive">
            <Table className="mb-0" hover>
              <thead className="bg-light">
                <tr>
                  {activeTab === 'audit' ? (
                    <>
                      <th className="border-0 py-3 px-4">Timestamp</th>
                      <th className="border-0 py-3">Table</th>
                      <th className="border-0 py-3">Operation</th>
                      <th className="border-0 py-3">Record ID</th>
                      <th className="border-0 py-3 text-center">Changes</th>
                    </>
                  ) : activeTab === 'reactions' ? (
                    <>
                      <th className="border-0 py-3 px-4">Timestamp</th>
                      <th className="border-0 py-3">Status</th>
                      <th className="border-0 py-3">Channel</th>
                      <th className="border-0 py-3">Message ID</th>
                      <th className="border-0 py-3">Emoji</th>
                      <th className="border-0 py-3">Type</th>
                      <th className="border-0 py-3">Message</th>
                      <th className="border-0 py-3">Status Code</th>
                      <th className="border-0 py-3">Latency (ms)</th>
                    </>
                  ) : (
                    <>
                      <th className="border-0 py-3 px-4">Timestamp</th>
                      <th className="border-0 py-3">Event Type</th>
                      <th className="border-0 py-3">Severity</th>
                      <th className="border-0 py-3">Message</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={activeTab === 'audit' ? 5 : activeTab === 'reactions' ? 9 : 4} className="text-center py-5">
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : currentLogs.length > 0 ? (
                  currentLogs.map((log) => (
                    <tr key={log.id} style={{ cursor: 'pointer' }}>
                      {activeTab === 'audit' ? (
                        <>
                          <td className="px-4 py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <code className="text-muted small">
                              {new Date(log.changed_at).toLocaleString()}
                            </code>
                          </td>
                          <td className="py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <span className="fw-medium">{log.table_name}</span>
                          </td>
                          <td className="py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <Badge bg={getOperationBadgeVariant(log.operation)} className="text-uppercase">
                              {log.operation}
                            </Badge>
                          </td>
                          <td className="py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <code>{log.record_id}</code>
                          </td>
                          <td className="py-3 text-center">
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                viewMessage(JSON.stringify(log.changed_data, null, 2));
                              }}
                            >
                              <Eye size={14} />
                            </Button>
                          </td>
                        </>
                      ) : activeTab === 'reactions' ? (
                        <>
                          <td className="px-4 py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <code className="text-muted small">
                              {new Date(log.created_at).toLocaleString()}
                            </code>
                          </td>
                          <td className="py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <Badge bg={getSuccessBadgeVariant(log.success)} className="text-uppercase">
                              {log.success ? 'Success' : 'Failed'}
                            </Badge>
                          </td>
                          <td className="py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <code>{log.channel_id}</code>
                          </td>
                          <td className="py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <code>{log.message_id}</code>
                          </td>
                          <td className="py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <span className="fw-medium">{formatEmoji(log.emoji)}</span>
                          </td>
                          <td className="py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <span className="text-muted">{log.reactor_type}</span>
                          </td>
                          <td className="py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <span 
                              className="text-truncate d-inline-block" 
                              style={{maxWidth: '200px', cursor: 'pointer'}}
                              onClick={(e) => {
                                e.stopPropagation();
                                viewMessage(log.error_message);
                              }}
                              title={log.error_message || 'No message'}
                            >
                              {truncateText(log.error_message, 30)}
                            </span>
                          </td>
                          <td className="py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <Badge bg={log.status_code >= 200 && log.status_code < 300 ? 'success' : 
                                      log.status_code >= 400 && log.status_code < 500 ? 'warning' : 
                                      log.status_code >= 500 ? 'danger' : 'secondary'}>
                              {log.status_code || 'N/A'}
                            </Badge>
                          </td>
                          <td className="py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <span className="text-muted">{log.latency_ms || 'N/A'} ms</span>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <code className="text-muted small">
                              {new Date(log.created_at).toLocaleString()}
                            </code>
                          </td>
                          <td className="py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <span className="fw-medium">{log.event_type}</span>
                          </td>
                          <td className="py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <Badge bg={getSeverityBadgeVariant(log.severity)} className="text-uppercase">
                              {log.severity}
                            </Badge>
                          </td>
                          <td className="py-3" onClick={() => toggleRowExpansion(log.id)}>
                            <span className="text-muted">{log.message}</span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={activeTab === 'audit' ? 5 : activeTab === 'reactions' ? 9 : 4} className="text-center py-5 text-muted">
                      No logs found matching your criteria
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="d-md-none">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : currentLogs.length > 0 ? (
              currentLogs.map((log) => {
                const isExpanded = expandedRows.has(log.id);
                
                return (
                  <Card key={log.id} className="mb-3 border-0 shadow-sm">
                    <Card.Body 
                      className="p-3"
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleRowExpansion(log.id)}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-1">
                            {activeTab === 'audit' ? (
                              <>
                                <Badge bg={getOperationBadgeVariant(log.operation)} className="text-uppercase me-1">
                                  {log.operation}
                                </Badge>
                                <span className="fw-medium small">{log.table_name}</span>
                              </>
                            ) : activeTab === 'reactions' ? (
                              <>
                                <Badge bg={getSuccessBadgeVariant(log.success)} className="text-uppercase me-1">
                                  {log.success ? 'Success' : 'Failed'}
                                </Badge>
                                <span className="text-muted small">{log.reactor_type}</span>
                              </>
                            ) : (
                              <>
                                <Badge bg={getSeverityBadgeVariant(log.severity)} className="text-uppercase me-1">
                                  {log.severity}
                                </Badge>
                                <span className="fw-medium small">{log.event_type}</span>
                              </>
                            )}
                          </div>
                          
                          <div className="text-muted small">
                            {new Date(log.changed_at || log.created_at).toLocaleString()}
                          </div>
                          
                          {activeTab === 'audit' && (
                            <div className="small mt-1">
                              <code>ID: {log.record_id}</code>
                            </div>
                          )}
                          
                          {activeTab === 'reactions' && (
                            <div className="small mt-1">
                              <div>Channel: <code>{log.channel_id}</code></div>
                              <div>Message ID: <code>{log.message_id}</code></div>
                              <div>Emoji: {formatEmoji(log.emoji)}</div>
                              {log.error_message && (
                                <div className="mt-1">
                                  <div className="text-muted small">Message:</div>
                                  <div 
                                    className="small text-truncate"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      viewMessage(log.error_message);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    {truncateText(log.error_message, 50)}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {activeTab === 'system' && (
                            <div className="small mt-1 text-truncate">
                              {log.message}
                            </div>
                          )}
                        </div>
                        <div>
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="mt-3 pt-3 border-top">
                          <Row className="g-3">
                            {activeTab === 'audit' && (
                              <Col xs={12} className="text-center">
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    viewMessage(JSON.stringify(log.changed_data, null, 2));
                                  }}
                                >
                                  <Eye size={14} className="me-1" />
                                  View Changes
                                </Button>
                              </Col>
                            )}
                            
                            {activeTab === 'reactions' && (
                              <>
                                <Col xs={6}>
                                  <div className="text-muted small">Status Code</div>
                                  <Badge bg={log.status_code >= 200 && log.status_code < 300 ? 'success' : 
                                            log.status_code >= 400 && log.status_code < 500 ? 'warning' : 
                                            log.status_code >= 500 ? 'danger' : 'secondary'}>
                                    {log.status_code || 'N/A'}
                                  </Badge>
                                </Col>
                                <Col xs={6}>
                                  <div className="text-muted small">Latency</div>
                                  <div className="small">{log.latency_ms || 'N/A'} ms</div>
                                </Col>
                              </>
                            )}
                          </Row>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                );
              })
            ) : (
              <div className="text-center py-5 text-muted">
                No logs found matching your criteria
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Message Modal */}
      <Modal show={showMessageModal} onHide={() => setShowMessageModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Message Content</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
            {currentMessage}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMessageModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Logs;